/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {RadioGroup} from "@headlessui/react";
import clsx from "clsx";
import {getDurationOptions, usePackageItemKartingOptions} from "utils/packageUtils";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import FeatherIcon from "components/FeatherIcon";
import {currencyNumberFormat} from "utils/utility";
import {useTypedSelector} from "helpers/reducers";

interface BookingPackagesTicketsSelectFamilyProps {
    chosenDuration: number | undefined;
    setChosenDuration: (val: number) => void;
    disabled1Hour: boolean;
    disabled2Hours: boolean;
}

/**
 * Unified ticket duration selector for family bookings.
 * Shows both adult and child prices per duration option.
 * One selection applies to all persons (adults get adult ticket, children get child ticket).
 */
const BookingPackagesTicketsSelectFamily = (props: BookingPackagesTicketsSelectFamilyProps) => {
    const {t} = useTranslation();
    const [chosenDuration, setChosenDuration] = useState<number | undefined>(props.chosenDuration);
    const {getPackageItemForCombination, getPackageItemPrice} = usePackageItemKartingOptions();
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const hasChildren = filter.children > 0;
    const allDurations = getDurationOptions(props.disabled2Hours, props.disabled1Hour);

    const isAvailable = (duration: number) => !!getPackageItemForCombination(duration, false, PackageItemPersonType.Adult);

    const visibleOptions = allDurations.filter(o => isAvailable(o.value));

    useEffect(() => {
        setChosenDuration(props.chosenDuration);
    }, [props.chosenDuration]);

    return (
        /* Mobile: 1 col full-width · sm: 2 col · lg+: flex-wrap with fixed card width */
        <RadioGroup
            className="BookingPackagesTicketsSelectFamily flex flex-col sm:flex-row sm:flex-wrap gap-2 my-2 w-full justify-center items-center sm:items-end"
            value={chosenDuration}
            onChange={props.setChosenDuration}
        >
            {visibleOptions.map(option => {
                const isRecommended = option.value === 270 || option.value === 240;
                const adultPrice = getPackageItemPrice(option.value, PackageItemPersonType.Adult);
                const childPrice = hasChildren ? getPackageItemPrice(option.value, PackageItemPersonType.Child) : null;
                const totalAdultPrice = filter.adults * adultPrice;
                const totalChildPrice = childPrice !== null ? filter.children * childPrice : 0;
                const isSelected = chosenDuration === option.value;
                const totalPriceForTicket = totalAdultPrice + totalChildPrice;

                return (
                    // <div key={option.value} className="flex flex-col justify-end w-full md:w-auto lg:w-[260px] md:flex-1 lg:flex-none">
                    <div key={option.value} className="xs:max-w-[300px] sm:max-w-[184px] md:max-w-[210px] flex flex-col justify-end w-full md:w-auto md:flex-1">
                        {isRecommended && (
                            <div
                                className={
                                    "rounded-t-xl flex flex-row w-full items-center justify-center gap-1.5 px-2 py-1.5 uppercase bg-[linear-gradient(90deg,#FFAE00_0%,#E1570B_35%,#D12C11_70%,#C20016_100%)] text-white"
                                }
                            >
                                <img src="/img/credits/top-recommendation-icon.svg" alt="top recommendation" className="size-[11px] shrink-0" />
                                <span className={"text-white text-[10px] font-bold leading-none whitespace-nowrap"}>{t("Top recommendation")}</span>
                            </div>
                        )}
                        <RadioGroup.Option
                            className={clsx(
                                "cursor-pointer grow-0 focus:outline-none w-full relative flex flex-col transition-all duration-150 shadow-sm border-2 px-3 py-3",
                                isRecommended ? "rounded-b-xl border-t-0 grow" : "rounded-xl",
                                isSelected ? "border-brand-yellow bg-brand-yellow/5" : "border-[#E5E5E5] hover:border-brand-yellow bg-white"
                            )}
                            value={option.value}
                        >
                            {/* Duration header row */}
                            <div className={"flex flex-row items-center justify-center gap-2 mb-3"}>
                                <div className={"border border-[#E5E5E5] rounded-full bg-white p-1"}>
                                    <FeatherIcon
                                        name={"check"}
                                        className={clsx("size-3 xs:size-4 sm:size-5 flex-none", isSelected ? "text-brand-yellow" : "text-transparent")}
                                        strokeWidth={3}
                                    />
                                </div>
                                <span className={"font-bold text-sm lg:text-base whitespace-nowrap leading-none"}>{option.name}</span>
                            </div>

                            {/* Divider */}
                            <div className={"w-full border-t border-dashed border-[#E5E5E5] mb-2.5"} />

                            {/* Person rows: icon + label left, price right */}
                            <div className={"flex flex-col gap-1"}>
                                {/* Adult */}
                                <div className={"flex flex-row items-center justify-between gap-3"}>
                                    <div className={"flex flex-row items-center gap-1.5 min-w-0"}>
                                        <span className={"flex items-center justify-center w-[20px] h-[20px] rounded-full bg-[#F1F2FA] shrink-0"}>
                                            <FeatherIcon name={"user"} className={"size-[12px] flex-none text-[#3E52C7]"} strokeWidth={3} />
                                        </span>
                                        <span className={"text-[10px] text-gray-600 truncate"}>{t("Adult")}</span>
                                    </div>
                                    <span className={"font-semibold text-[10px] text-black shrink-0"}>
                                        {filter.adults} x {currencyNumberFormat(adultPrice)}
                                    </span>
                                </div>

                                {/* Child */}
                                {childPrice !== null && (
                                    <div className={"flex flex-row items-center justify-between gap-3"}>
                                        <div className={"flex flex-row items-center gap-1.5 min-w-0"}>
                                            <span className={"flex items-center justify-center w-[20px] h-[20px] rounded-full bg-[#F1F2FA] shrink-0"}>
                                                <FeatherIcon name={"user"} className={"size-[12px] flex-none text-[#79D00E]"} strokeWidth={3} />
                                            </span>
                                            <span className={"text-[10px] text-gray-600 truncate"}>{t("Child")}</span>
                                        </div>
                                        <span className={"font-semibold text-[10px] text-black shrink-0"}>
                                            {filter.children} x {currencyNumberFormat(childPrice)}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Divider */}
                            <div className={"w-full border-t border-dashed border-[#E5E5E5] mt-2 mb-2.5"} />

                            {/* total price */}
                            <div className={"flex flex-row items-center justify-between"}>
                                <span className={"text-xs font-medium"}>{t("Total")}</span>
                                <span className={"text-sm font-bold"}>{currencyNumberFormat(totalPriceForTicket)}</span>
                            </div>
                        </RadioGroup.Option>
                    </div>
                );
            })}
        </RadioGroup>
    );
};

export default BookingPackagesTicketsSelectFamily;
